import {
    EzException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzNumber,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

import { EzEmployerOptionInfo } from '/ezlibrary/entities/EzEmployerOptionInfo.js';
import { EzClockerAccountType } from '/ezlibrary/EzClockerAccountType.js';
import { EzPayPeriod } from '/ezlibrary/enums/EzPayPeriod';
import { EzRoundingMethod } from '/ezlibrary/enums/EzRoundingMethod.js';

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * Enumeration of available employer option keys with default values.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES:
 * Currently not included in the EzEnumerations.js bundle
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzEmployerOption } from '/ezlibrary/EzEmployerOption.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzEmployerOption extends EzEnumeration2 {
    /**
     * @static
     * @public @readonly @property
     * Gets the default string value that represents true for a employer option
     *
     * @returns {string}
     */
    static get EZOPTION_VALUE_TRUE() {
        return '1';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default string value that represents false for a employer option
     *
     * @returns {string}
     */
    static get EZOPTION_VALUE_FALSE() {
        return '0';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default string value that represents an empty option
     *
     * @returns {string}
     */
    static get EZOPTION_VALUE_EMPTY() {
        return EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Pre-pends 'EZOPTION_' to the enumeration value.
     *
     * @param {String} enumnValue
     *
     * @returns {String}
     */
    static ezToInputId(ezEmployerOptionValue) {
        return `EZOPTION_${ezEmployerOptionValue.toUpperCase()}`;
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the
     * EzClockerAccountType.EMPLOYER account type.
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToEmployerOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected Integration
            case EzEmployerOption.EZOPTION_SELECTED_INTEGRATION:
                return EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_INTEGRATION;

            // Selected employee id
            case EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_PAYROL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID:
                return EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID;

            // Employer last selected schedule week
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD;

            // Selected Period Start
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE:
                return EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE;

            // Selected Period End
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE:
                return EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE;

            // Show Day Totals
            case EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS;

            // Use 24 hour time in export
            case EzEmployerOption.EZOPTION_USE_24HOUR_TIME_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // Use decimal totals in export
            case EzEmployerOption.EZOPTION_TOTALS_AS_DECIMAL_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS;

            // Preferred timezone
            case EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
                return EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE;

            // Group jobs report by job
            case EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
                return EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB;

            // Jobs report group by date
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_DATE:
                return EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_DATE;

            // Jobs report group by employee
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
                return EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE;

            // Copy notes during schedule copy forward
            case EzEmployerOption.EZOPTION_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
                return EzEmployerOption.EZOPTION_EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the
     * EzClockerAccountType.MANAGER_AS_EMPLOYER account type (acting as a Employer).
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToManagerAsEmployerOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected employee id
            case EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID;

            // Schedule selected week
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD;

            // Selected period start date
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE;

            // Selected Period End
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE;

            // Push notifications
            case EzEmployerOption.EZOPTION_ENABLE_PUSH_NOTIFICATIONS:
            case EzEmployerOption.EZOPTION_PUSH_NOTIFICATIONS_ENABLED:
            case EzEmployerOption.EZOPTION_MANAGER_PUSH_NOTIFICATIONS_ENABLED:
                return EzEmployerOption.EZOPTION_MANAGER_PUSH_NOTIFICATIONS_ENABLED;

            case EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS;

            // Use 24 hour time in exports
            case EzEmployerOption.EZOPTION_USE_24HOUR_TIME_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // Use decimal in export totals
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_TOTALS_AS_DECIMAL_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS;

            // Preferred timezone
            case EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE;

            // Group jobs report by job
            case EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
                return EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB;

            // Jobs report group by date
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_DATE:
                return EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_DATE;

            // Jobs report group by employee
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
                return EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE;

            // Copy notes during schedule copy forward
            case EzEmployerOption.EZOPTION_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
                return EzEmployerOption.EZOPTION_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the
     * EzClockerAccountType.MANAGER account type (acting as a employee).
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToManagerOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected Integration
            case EzEmployerOption.EZOPTION_SELECTED_INTEGRATION:
                return EzEmployerOption.EZOPTION_MANAGER_SELECTED_INTEGRATION;

            // Selected schedule week
            case EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD;

            // Use 24 hour in exports
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the
     * EzClockerAccountType.PAYROLL_MANAGER_AS_EMPLOYER account type.
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToPayrollManagerAsEmployerOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected employee id
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID:
            case EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID;

            // Schedule selected week
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD;

            // Selected period start date
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE;

            // Selected Period End
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE;

            // Push notifications
            case EzEmployerOption.EZOPTION_ENABLE_PUSH_NOTIFICATIONS:
            case EzEmployerOption.EZOPTION_PUSH_NOTIFICATIONS_ENABLED:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_PUSH_NOTIFICATIONS_ENABLED;

            // Use 24 hour time in exports
            case EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS;

            // Use 24-hour time in reports
            case EzEmployerOption.EZOPTION_USE_24HOUR_TIME_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // Use decimal in export totals
            case EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
            case EzEmployerOption.EZOPTION_TOTALS_AS_DECIMAL_IN_REPORTS:
            case EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS;

            // Preferred timezone
            case EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE:
            case EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE;

            // Group jobs report by job
            case EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB;

            // Jobs report group by date
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_DATE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_DATE:
                return EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_DATE;

            // Jobs report group by employee
            case EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
            case EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE:
                return EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_EMPLOYEE;

            // Copy notes during schedule copy forward
            case EzEmployerOption.EZOPTION_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
      * @static
      * @public @method
      * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the EzClockerAccountType.PAYROLL_MANAGER account type.
      * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
      *
      * @param {string} ezEmployerOptionValue
      * A valid enumeration property value from EzEmployerOption
      *
      * @returns {string}
      * A valid enumeration property value from EzEmployerOption
      */
    static ezToPayrollManagerOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected Integration
            case EzEmployerOption.EZOPTION_SELECTED_INTEGRATION:
                return EzEmployerOption.EZOPTION_PAYROLL_MANAGER_SELECTED_INTEGRATION;

            // Selected schedule week
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD;

            // Show day totals in export reports
            case EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS;

            // Use 24 hour in exports
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlant EzEmployerOption value for the EzClockerAccountType.EMPLOYEE account type.
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToEmployeeOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        switch (EzEmployerOption.ezAsEnum(ezEmployerOptionValue)) {
            // Selected schedule week
            case EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK:
            case EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK:
                return EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK;

            // Selected period
            case EzEmployerOption.EZOPTION_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_PAYROLL_EMPLOYER_SELECTED_PERIOD:
            case EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD:
                return EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD;

            // Use 24 hour in exports
            case EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS:
                return EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS;

            // All other option names return as-is
            default:
                return EzEmployerOption.ezAsEnum(ezEmployerOptionValue);
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the EzEmployerOption enum property value to the equivlant value for a EzClockerAccountType.PERSONAL
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOptionValue
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption
     */
    static ezToPersonalOption(ezEmployerOptionValue) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        return EzEmployerOption.ezToEmployeeOption(
            EzEmployerOption.ezAsEnum(ezEmployerOptionValue));
    }

    /**
     * @static
     * @public @method
     * Transforms the provided ezEmployerOptionValue to the equivlent option key based upon the active user's account type (EzClockerAccountType)
     * If the provdied ezEmployerOptionValue is not a valid EzEmployerOption enumeration property value, then EzEmployerOption.UNKNOWN is returned.
     *
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption for the
     * current user context's account type.
     */
    static ezToOptionForAccountType(ezEmployerOptionValue) {
        if (!EzObject.isValid(globalThis.ezApi.ezclocker?.ezClockerContext?.ezUserContext)) {
            throw new EzException(
                EzString.em`
                    A valid user context from globalThis.ezApi.ezclocker?.ezClockerContext?.ezUserContext is required
                    before calling static method ${EzEmployerOption.ezToOptionForAccountType.toString()}.`);
        }

        if (!EzString.hasLength(ezEmployerOptionValue)) {
            return EzEmployerOption.UNKNOWN;
        }

        return EzEmployerOption.ezOptionKeyForAccountType(
            EzEmployerOption.ezAsEnum(ezEmployerOptionValue),
            EzClockerAccountType.ezCurrentUserEzClockerAccountType);
    }

    /**
     * @static
     * @public @method
     * Gets the option key to use for the provided ezEmployerOption (or generic option key) and
     * provided ezClockerAccountType.
     * If the ezClockerAccountType does not have a account specific key for the provided ezEmployerOption,
     * then the provided ezEmployerOption is returned as is.
     *
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     * @param {string} ezClockerAccountType
     * A valid enumeration property value from EzClockerAccountType
     * Default: EzClockerAccountType.ezCurrentUserEzClockerAccountType
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption for the current user context account type.
     */
    static ezOptionKeyForAccountType(ezEmployerOptionValue = EzEmployerOption.UNKNOWN,
        ezClockerAccountType = EzClockerAccountType.ezCurrentUserEzClockerAccountType) {
        if (!EzString.hasLength(ezEmployerOptionValue)) {
            throw new EzBadParamException(
                'ezEmployerOptionValue',
                EzEmployerOption,
                EzEmployerOption.ezOptionKeyForAccountType);
        }

        ezEmployerOptionValue = EzEmployerOption.ezAsEnum(ezEmployerOptionValue);

        if (EzEmployerOption.UNKNOWN === ezEmployerOptionValue) {
            throw new EzBadParamException(
                'ezEmployerOption',
                EzEmployerOption,
                EzEmployerOption.ezOptionKeyForAccountType,
                `${ezEmployerOptionValue} does not appear to be a valid EzEmployerOption enumeration property value.`);
        }

        switch (ezClockerAccountType) {
            case EzClockerAccountType.EMPLOYER:
                return EzEmployerOption.ezToEmployerOption(ezEmployerOptionValue);
            case EzClockerAccountType.MANAGER_AS_EMPLOYER:
                return EzEmployerOption.ezToManagerAsEmployerOption(ezEmployerOptionValue);
            case EzClockerAccountType.MANAGER:
                return EzEmployerOption.ezToManagerOption(ezEmployerOptionValue);
            case EzClockerAccountType.PAYROLL_MANAGER:
                return EzEmployerOption.ezToPayrollManagerOption(ezEmployerOptionValue);
            case EzClockerAccountType.PAYROLL_MANAGER_AS_EMPLOYER:
                return EzEmployerOption.ezToPayrollManagerAsEmployerOption(ezEmployerOptionValue);
            case EzClockerAccountType.EMPLOYEE:
                return EzEmployerOption.ezToEmployeeOption(ezEmployerOptionValue);
            case EzClockerAccountType.PERSONAL:
                return EzEmployerOption.ezToPersonalOption(ezEmployerOptionValue);
            case EzClockerAccountType.ADMIN:
            case EzClockerAccountType.SUPPORT:
            case EzClockerAccountType.DEVELOPER:
            default:
                return ezEmployerOptionValue;
        }
    }

    /**
     * @public @method
     * 1) If the provided ezClockerAccountType is PAYROLL_MANAGER, MANAGER, EMPLOYEE< or PERSONAL
     *    then the provided ezEmployerOption is returned using template: `${aAccountId.toString()}_${ezEmployerOption}`
     *
     * 2) If the provided ezClockerAccountType is UNKNOWN, ADMIN, SUPPORT, DEVELOPER, EMPLOYER, PAYROLL_MANAGER_AS_EMPLOYER, or MANAGER_AS_EMPLOYER
     *    then the provided ezEmployerOption value is returned AS IS.
     *
     * @param {string} ezClockerAccountType
     * A valid enumeration property value from EzClockerAccountType
     * @param {string|number} accountId
     * The id from the account entity (Employer, Employee)
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption with the provided aAccountId prepended according to the template:
     */
    static ezPrependAccountIdToOptionKey(ezClockerAccountType = EzClockerAccountType.UNKNOWN, accountEntityId = null, aOptionKey = EzEmployerOption.UNKNOWN) {
        if (!EzString.hasLength(ezClockerAccountType) || EzClockerAccountType.UNKNOWN === ezClockerAccountType) {
            throw new EzBadParamException(
                'ezClockerAccountType',
                EzEmployerOption,
                EzEmployerOption.ezPrependAccountIdToOptionKey);
        }
        if (!EzString.hasLength(aOptionKey) || EzEmployerOption.UNKNOWN === aOptionKey) {
            throw new EzBadParamException(
                'aOptionKey',
                EzEmployerOption,
                EzEmployerOption.ezPrependAccountIdToOptionKey);
        }

        /**
         * The account id is prepended to the option key for the following account types:
         *  1) EzClockerAccountType.PAYROLL_MANAGER_AS_EMPLOYER (Payroll Manager acting as a Employer account) (Role: EzUserRole.ROLE_PAYROLL_MANAGER)
         *  2) EzClockerAccountType.PAYROLL_MANAGER (Payroll Manager acting as a their own Employee account) (Role: EzUserRole.ROLE_EMPLOYEE)
         *  3) EzClockerAccountType.MANAGER_AS_EMPLOYER (Payroll Manager acting as a Employer account) (Role: EzUserRole.ROLE_MANAGER)
         *  4) EzClockerAccountType.MANAGER (Manager acting as a their own Employee account) (Role: EzUserRole.ROLE_EMPLOYEE)
         *  5) EzClockerAccountType.EMPLOYEE (Role: EzUserRole.ROLE_EMPLOYEE)
         *  6) EzClockerAccountType.PERSONAL (Role: EzUserRole.ROLE_PERSONAL, EzUserRole.ROLE_EMPLOYEE)
         */
        if (EzClockerAccountType.ezEmployeeAccountTypes.includes(ezClockerAccountType) ||
            EzClockerAccountType.ezActingAsEmployerAccountTypes.includes(ezClockerAccountType)) {
            if (!EzNumber.isNumber(accountEntityId)) {
                throw new EzBadParamException(
                    'accountEntityId',
                    EzEmployerOption,
                    EzEmployerOption.ezPrependAccountIdToOptionKey);
            }

            return `${EzNumber.asString(accountEntityId)}_${aOptionKey}`;
        }

        /* The accountId is ignored and not prepended to the option key for For the following account types:
         *  1) EzClockerAccountType.EMPLOYER (Role: EzUserRole.ROLE_EMPLOYER)
         *  4) EzClockerAccountType.DEVELOPER (Role: EzUserRole.ROLE_DEVELOPER)
         *  5) EzClockerAccountType.SUPPORT (Role: EzUserRole.ROLE_EZCLOCKER_SUPPORT)
         *  6) EzClockerAccountType.ADMIN (Role: EzUserRole.ROLE_ADMIN)
         *  7) EzClockerAccountType.UNKNOWN (Role: EzUserRole.UNKNOWN)
         */
        return aOptionKey;
    }

    /**
     * @public @method
     * Gets the option key name to use for a given option name. Some accounts support two different sets
     * of options (manager account as the prime example) and need to swtich sets in on the employer dashboard vs
     * an employee dashboard.
     *
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption for the current user context account type.
     *
     * @deprecated
     * Migrate to: EzEmployerOption.ezToOptionForAccountType(ezEmployerOption);
    */
    static ezGetActiveAccountOptionKeyNameForOptionName(ezEmployerOption = EzEmployerOption.UNKNOWN) {
        return EzEmployerOption.ezToOptionForAccountType(ezEmployerOption);
    }

    /**
     * @public @method
     * Gets the default typed value of a EzEmployerOption.
     * If the provided ezEmployerOption param results in EzEmployerOption.UNKNOWN
     * then a bad param exception is thrown.
     *
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     *
     * @returns {EzEnumData}
     */
    static ezEzEmployerOptionDefaultTypedValue(ezEmployerOption = EzEmployerOption.UNKNOWN) {
        const ezEmployerOptionEnum = EzEmployerOption.ezAsEnum(ezEmployerOption);

        if (EzEmployerOption.UNKNOWN === ezEmployerOptionEnum) {
            throw new EzBadParamException(
                'ezEmployerOption',
                EzEmployerOption,
                EzEmployerOption.ezEzEmployerOptionDefaultTypedValue,
                `The provided ezEmployerOption of '${ezEmployerOption}' is not a valid EzEmployerOption enumeration property value.`);
        }

        return EzEmployerOption.ezEnumData(ezEmployerOptionEnum).ezDefaultTypedValue;
    }

    /**
     * @public @method
     * Gets the default typed value (normally a string, but can be a JSON Object, number, or null) of the provided EzEmployerOption.
     * If the provided ezEmployerOption param results in EzEmployerOption.UNKNOWN then a bad param exception is thrown.
     *
     * @param {string} ezEmployerOption
     * A valid enumeration property value from EzEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     *
     * @returns {EzEnumData}
     */
    static ezEzEmployerOptionDefaultValue(ezEmployerOption = EzEmployerOption.UNKNOWN) {
        const ezEmployerOptionEnum = EzEmployerOption.ezAsEnum(ezEmployerOption);

        if (EzEmployerOption.UNKNOWN === ezEmployerOptionEnum) {
            throw new EzBadParamException(
                'ezEmployerOption',
                EzEmployerOption,
                EzEmployerOption.ezEzEmployerOptionDefaultValue,
                `The provided ezEmployerOption of '${ezEmployerOption}' is not a valid EzEmployerOption enumeration property value.`);
        }

        return EzEmployerOption.ezEnumData(ezEmployerOptionEnum).ezDefaultValue;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @constructor
     * >> DO NOT CREATE NEW INSTANCES <<
     * The static initializer will create a singleton instance that injects the static properties and methods
     * that you then use to access enumeration values.
     */
    constructor(name, value, optionalData) {
        super(name, value, optionalData);
    }

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzEmployerOption}
     */
    static #ezEmployerOptionEnumerationSingleton = null;

    /**
     * @static
     * @private @method
     * Initializes the static properties, fields, and methods for the enumeration class EzExportAction
     */
    static #ezInit() {
        this.#ezEmployerOptionEnumerationSingleton = new EzEmployerOption();

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Generic Account Option Names
            Generic option names can be used for all accounts types if readin/writing the option is through the EzClockerContext.
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            'UNKNOWN',
            'UNKNOWN',
            new EzEmployerOptionInfo(
                'UNKNOWN',
                'UNKNOWN',
                'UNKNOWN'));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Generic Account Option Names
            Generic option names can be used for all accounts types if readin/writing the option is through the EzClockerContext.
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */
        const ezOptionPrefix = 'EZOPTION_';

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Account Transformed Values for EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID
                Each account type will transform EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID to their own
                specific value.

                    Employer Accounts Signed In with Role:
                        1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID

                    Payroll Manager Accounts Signed In with Role:
                        1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID
                        2) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID)

                    Manager Accounts Signed In with Role:
                        1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID
                        1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID)

                    Employee Accounts Signed In with Role:
                        1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID)

                    Personal Accounts Signed In with Role:
                        1) ROLE_PERSIONAL: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID)
                        1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_EMPLOYEE_ID)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_EMPLOYEE_ID`,
            'SELECTED_EMPLOYEE_ID',
            new EzEmployerOptionInfo(
                'SELECTED_EMPLOYEE_ID',
                EzNumber.MINUS_ONE.toString(),
                EzNumber.MINUS_ONE));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Account Transformed Values for EzEmployerOption.EZOPTION_SELECTED_PERIOD
                Each account type will transform EzEmployerOption.EZOPTION_SELECTED_PERIOD to their own
                specific value.

                    Employer Accounts Signed In with Role:
                        1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD

                    Payroll Manager Accounts Signed In with Role:
                        1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD
                        2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD

                    Manager Accounts Signed In with Role:
                        1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD
                        1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD

                    Employee Accounts Signed In with Role:
                        1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD

                    Personal Accounts Signed In with Role:
                        1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD
                        1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_PERIOD`,
            'SELECTED_PERIOD',
            new EzEmployerOptionInfo(
                'SELECTED_PERIOD',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            !! Important !!
                1) EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK is actually
                   'GENERIC_ACCOUNT_SCHEDULE_WEEK' and is NOT (no longer) 'SELECTED_SCHEDULE_WEEK'.
                2) You might find a employer with an option using the LEGACY
                   name of 'SELECTED_SCHEDULE_WEEK' the legacy option is no longer used.
            ------------------------------------------------------------------------
            [ Account Transformed Values for EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK ]
            Each account type will transform EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.SELECTED_SCHEDULE_WEEK

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK
                    2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_SCHEDULE_WEEK`,
            'EZOPTION_GENERIC_ACCOUNT_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'EZOPTION_GENERIC_ACCOUNT_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_START_ISO)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_PERIOD_START_ISO`,
            'SELECTED_PERIOD_START_ISO',
            new EzEmployerOptionInfo(
                'SELECTED_PERIOD_START_ISO',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_SELECTED_PERIOD_END_ISO)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_PERIOD_END_ISO`,
            'SELECTED_PERIOD_END_ISO',
            new EzEmployerOptionInfo(
                'SELECTED_PERIOD_END_ISO',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ENABLE_PUSH_NOTIFICATIONS`,
            'ENABLE_PUSH_NOTIFICATIONS',
            new EzEmployerOptionInfo(
                'ENABLE_PUSH_NOTIFICATIONS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_USE_24HOUR_TIME_IN_REPORTS to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS
                    2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}USE_24HOUR_TIME_IN_REPORTS`,
            'USE_24HOUR_TIME_IN_REPORTS',
            new EzEmployerOptionInfo(
                'USE_24HOUR_TIME_IN_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TOTALS_AS_DECIMAL_IN_REPORTS`,
            'TOTALS_AS_DECIMAL_IN_REPORTS',
            new EzEmployerOptionInfo(
                'TOTALS_AS_DECIMAL_IN_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PREFERRED_TIMEZONE`,
            'PREFERRED_TIMEZONE',
            new EzEmployerOptionInfo(
                'PREFERRED_TIMEZONE',
                'AUTOMATIC',
                'AUTOMATIC'));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (results in EzEmployerOption.EZOPTION_GROUP_JOBS_REPORT_BY_JOB)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}GROUP_JOBS_REPORT_BY_JOB`,
            'GROUP_JOBS_REPORT_BY_JOB',
            new EzEmployerOptionInfo(
                'GROUP_JOBS_REPORT_BY_JOB',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));
        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            !! Important !!
            EzEmployerOption.EZOPTION_SELECTED_INTEGRATION is actually 'GENERIC_SELECTED_INTEGRATION'.
            ------------------------------------------------------------------------
            Account Transformed Values
            Each account type will transform EzEmployerOption.EZOPTION_SELECTED_SCHEDULE_WEEK to their own
            specific value:
                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_INTEGRATION

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_SELECTED_INTEGRATION
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_SELECTED_INTEGRATION
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_INTEGRATION`,
            'GENERIC_SELECTED_INTEGRATION',
            new EzEmployerOptionInfo(
                'GENERIC_SELECTED_INTEGRATION',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_DATE

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_DATE
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_DATE
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_DATE)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}JOBS_REPORT_GROUP_BY_DATE`,
            'JOBS_REPORT_GROUP_BY_DATE',
            new EzEmployerOptionInfo(
                'JOBS_REPORT_GROUP_BY_DATE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_JOBS_REPORT_GROUP_BY_EMPLOYEE
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_JOBS_REPORT_GROUP_BY_EMPLOYEE)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}JOBS_REPORT_GROUP_BY_EMPLOYEE`,
            'JOBS_REPORT_GROUP_BY_EMPLOYEE',
            new EzEmployerOptionInfo(
                'JOBS_REPORT_GROUP_BY_EMPLOYEE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CUSTOM_PERIOD_START_TIME_ENABLED`,
            'CUSTOM_PERIOD_START_TIME_ENABLED',
            new EzEmployerOptionInfo(
                'CUSTOM_PERIOD_START_TIME_ENABLED',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CUSTOM_PERIOD_START_TIME`,
            'CUSTOM_PERIOD_START_TIME',
            new EzEmployerOptionInfo(
                'CUSTOM_PERIOD_START_TIME',
                '00:00',
                '00:00'));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Employer Account Option Names
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_PERIOD_STARTDATE`,
            'EMPLOYER_SELECTED_PERIOD_STARTDATE',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_PERIOD_STARTDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_PERIOD_ENDDATE`,
            'EMPLOYER_SELECTED_PERIOD_ENDDATE',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_PERIOD_ENDDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_PERIOD`,
            'EMPLOYER_SELECTED_PERIOD',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_PERIOD',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_EMPLOYEE_ID`,
            'EMPLOYER_SELECTED_EMPLOYEE_ID',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_EMPLOYEE_ID',
                EzNumber.MINUS_ONE.toString(),
                EzNumber.MINUS_ONE));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PUSH_NOTIFICATIONS_ENABLED`,
            'PUSH_NOTIFICATIONS_ENABLED',
            new EzEmployerOptionInfo(
                'PUSH_NOTIFICATIONS_ENABLED',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK`,
            'EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS`,
            'USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            !! DEPRECATED !!
            Migrate to using: EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
            ------------------------------------------------------------------------
            Each account type will transform EzEmployerOption.EZOPTION_TOTALS_AS_DECIMAL_IN_REPORTS to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TOTALS_AS_DECIMAL_IN_REPORTS`,
            'TOTALS_AS_DECIMAL_IN_REPORTS',
            new EzEmployerOptionInfo(
                'TOTALS_AS_DECIMAL_IN_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS
                    2) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS
                    1) ROLE_EMPLOYEE: EzEmployerOption.EZOPTION_EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SHOW_DAY_TOTALS_IN_EXPORT_REPORTS`,
            'SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE`,
            'EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
            new EzEmployerOptionInfo(
                'EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
                'AUTOMATIC',
                'AUTOMATIC'));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_INTEGRATION`,
            'EMPLOYER_SELECTED_INTEGRATION',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_INTEGRATION',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_JOBS_REPORT_GROUP_BY_JOB`,
            'EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
            new EzEmployerOptionInfo(
                'EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_JOBS_REPORT_GROUP_BY_DATE`,
            'EMPLOYER_JOBS_REPORT_GROUP_BY_DATE',
            new EzEmployerOptionInfo(
                'EMPLOYER_JOBS_REPORT_GROUP_BY_DATE',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE`,
            'EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
            new EzEmployerOptionInfo(
                'EMPLOYER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD`,
            'EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
            new EzEmployerOptionInfo(
                'EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Payroll Manager Accounts
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_SELECTED_INTEGRATION`,
            'PAYROLL_MANAGER_SELECTED_INTEGRATION',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_SELECTED_INTEGRATION',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE`,
            'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE`,
            'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID`,
            'PAYROLL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID',
                EzNumber.MINUS_ONE.toString(),
                EzNumber.MINUS_ONE));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD`,
            'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_SELECTED_PERIOD',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK`,
            'PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_PUSH_NOTIFICATIONS_ENABLED`,
            'PAYROLL_MANAGER_PUSH_NOTIFICATIONS_ENABLED',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_PUSH_NOTIFICATIONS_ENABLED',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS`,
            'PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS`,
            'PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE`,
            'PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB`,
            'PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_EMPLOYER_SELECTED_INTEGRATION`,
            'PAYROLL_MANAGER_EMPLOYER_SELECTED_INTEGRATION',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_EMPLOYER_SELECTED_INTEGRATION',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_DATE`,
            'PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_DATE',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_DATE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE`,
            'PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD`,
            'PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
            new EzEmployerOptionInfo(
                'PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Manager Accounts
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_SELECTED_INTEGRATION`,
            'MANAGER_SELECTED_INTEGRATION',
            new EzEmployerOptionInfo(
                'MANAGER_SELECTED_INTEGRATION',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE`,
            'MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_SELECTED_PERIOD_STARTDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE`,
            'MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_SELECTED_PERIOD_ENDDATE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID`,
            'MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_SELECTED_EMPLOYEE_ID',
                EzNumber.MINUS_ONE.toString(),
                EzNumber.MINUS_ONE));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_SELECTED_PERIOD`,
            'MANAGER_EMPLOYER_SELECTED_PERIOD',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_SELECTED_PERIOD',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK`,
            'MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_PUSH_NOTIFICATIONS_ENABLED`,
            'MANAGER_PUSH_NOTIFICATIONS_ENABLED',
            new EzEmployerOptionInfo(
                'MANAGER_PUSH_NOTIFICATIONS_ENABLED',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS`,
            'MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'MANAGER_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'MANAGER_USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS`,
            'MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'MANAGER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE`,
            'MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB`,
            'MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
            new EzEmployerOptionInfo(
                'MANAGER_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_JOBS_REPORT_GROUP_BY_DATE`,
            'MANAGER_JOBS_REPORT_GROUP_BY_DATE',
            new EzEmployerOptionInfo(
                'MANAGER_JOBS_REPORT_GROUP_BY_DATE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE`,
            'MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
            new EzEmployerOptionInfo(
                'MANAGER_JOBS_REPORT_GROUP_BY_EMPLOYEE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD`,
            'MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
            new EzEmployerOptionInfo(
                'MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Employee and Personal Account Options
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_SELECTED_PERIOD`,
            'EMPLOYEE_SELECTED_PERIOD',
            new EzEmployerOptionInfo(
                'EMPLOYEE_SELECTED_PERIOD',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK`,
            'EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS`,
            'EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYEE_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS`,
            'EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYEE_SHOW_DAY_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Kiosk Constants
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}KIOSK_AUTO_LOGOUT_SECONDS`,
            'KIOSK_AUTO_LOGOUT_SECONDS',
            new EzEmployerOptionInfo(
                'KIOSK_AUTO_LOGOUT_SECONDS',
                EzNumber.TEN.toString(),
                EzNumber.TEN));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Global
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            The actual value for EzEmployerOption.EZOPTION_EMPLOYER_SCHEDULE_START_DAY is
            'startDay' and NOT 'EMPLOYER_SCHEDULE_START_DAY'. For some employers, the
            'EMPLOYER_SCHEDULE_START_DAY' option might exist, but this was a defect where it
            should have continued to use the older leagacy value 'startDay'.
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SCHEDULE_START_DAY`,
            'startDay',
            new EzEmployerOptionInfo(
                'startDay',
                EzNumber.ZERO.toString(),
                EzNumber.ZERO));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}REQUIRE_LOCATION_FOR_CLOCKINOUT`,
            'REQUIRE_LOCATION_FOR_CLOCKINOUT',
            new EzEmployerOptionInfo(
                'REQUIRE_LOCATION_FOR_CLOCKINOUT',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_DISABLE_TIME_ENTRY_EDITING`,
            'EMPLOYER_DISABLE_TIME_ENTRY_EDITING',
            new EzEmployerOptionInfo(
                'EMPLOYER_DISABLE_TIME_ENTRY_EDITING',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_OVERTIME_IN_TIME_SHEET_EXPORTS`,
            'CALCULATE_OVERTIME_IN_TIME_SHEET_EXPORTS',
            new EzEmployerOptionInfo(
                'CALCULATE_OVERTIME_IN_TIME_SHEET_EXPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_WEEKLY_OVERTIME_AFTER_HOURS`,
            'CALCULATE_WEEKLY_OVERTIME_AFTER_HOURS',
            new EzEmployerOptionInfo(
                'CALCULATE_WEEKLY_OVERTIME_AFTER_HOURS',
                EzNumber.FOURTY.toString(),
                EzNumber.FOURTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_DAILY_OVERTIME`,
            'CALCULATE_DAILY_OVERTIME',
            new EzEmployerOptionInfo(
                'CALCULATE_DAILY_OVERTIME',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_DAILY_OVERTIME_AFTER_HOURS`,
            'CALCULATE_DAILY_OVERTIME_AFTER_HOURS',
            new EzEmployerOptionInfo(
                'CALCULATE_DAILY_OVERTIME_AFTER_HOURS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_DAILY_DOUBLE_TIME`,
            'CALCULATE_DAILY_DOUBLE_TIME',
            new EzEmployerOptionInfo(
                'CALCULATE_DAILY_DOUBLE_TIME',
                EzNumber.EIGHT.toString(),
                EzNumber.EIGHT));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}CALCULATE_DAILY_DOUBLE_TIME_AFTER_HOURS`,
            'CALCULATE_DAILY_DOUBLE_TIME_AFTER_HOURS',
            new EzEmployerOptionInfo(
                'CALCULATE_DAILY_DOUBLE_TIME_AFTER_HOURS',
                EzNumber.TWELEVE.toString(),
                EzNumber.TWELEVE));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}RESTRICT_CLOCK_IN_TO_SCHEDULE`,
            'RESTRICT_CLOCK_IN_TO_SCHEDULE',
            new EzEmployerOptionInfo(
                'RESTRICT_CLOCK_IN_TO_SCHEDULE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ALLOW_EARLY_CLOCK_AT_MINS_BEFORE_SCHEDULE`,
            'ALLOW_EARLY_CLOCK_AT_MINS_BEFORE_SCHEDULE',
            new EzEmployerOptionInfo(
                'ALLOW_EARLY_CLOCK_AT_MINS_BEFORE_SCHEDULE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ROUND_CLOCK_IN_CLOCK_OUT`,
            'ROUND_CLOCK_IN_CLOCK_OUT',
            new EzEmployerOptionInfo(
                'ROUND_CLOCK_IN_CLOCK_OUT',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ROUND_CLOCK_IN_CLOCK_OUT_OPTION`,
            'ROUND_CLOCK_IN_CLOCK_OUT_OPTION',
            new EzEmployerOptionInfo(
                'ROUND_CLOCK_IN_CLOCK_OUT_OPTION',
                EzRoundingMethod.NEAREST_15,
                EzRoundingMethod.NEAREST_15));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ALLOW_RECORDING_OF_UNPAID_BREAKS`,
            'ALLOW_RECORDING_OF_UNPAID_BREAKS',
            new EzEmployerOptionInfo(
                'ALLOW_RECORDING_OF_UNPAID_BREAKS',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ALLOW_AUTOMATIC_BREAKS`,
            'ALLOW_AUTOMATIC_BREAKS',
            new EzEmployerOptionInfo(
                'ALLOW_AUTOMATIC_BREAKS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            AUTO_BREAK_WORK_HOURS_OPTION
            1) A positive integer from 1 to 24 as a number or the number as a string.
            Default value: 4
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}AUTO_BREAK_WORK_HOURS_OPTION`,
            'AUTO_BREAK_WORK_HOURS_OPTION',
            new EzEmployerOptionInfo(
                'AUTO_BREAK_WORK_HOURS_OPTION',
                EzNumber.FOUR.toString(),
                EzNumber.FOUR));

        /*
            AUTO_BREAK_WORK_MINUTES_OPTION
            Possible values:
            1) A positive integer from 1 to 30 as a number or a number as a string.
            Default: 30
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}AUTO_BREAK_WORK_MINUTES_OPTION`,
            'AUTO_BREAK_WORK_MINUTES_OPTION',
            new EzEmployerOptionInfo(
                'AUTO_BREAK_WORK_MINUTES_OPTION',
                EzNumber.THIRTY.toString(),
                EzNumber.THIRTY));

        /*
            NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT
            Possible values:
            1) 0 or '0' - number or number as string
            2) 5 or '5' - number or number as string
            3) 15 or '15' - number or number as string
            4) 30 or '30' - number or number as string
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT`,
            'NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            new EzEmployerOptionInfo(
                'NOTIFY_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                EzNumber.ZERO.toString(),
                EzNumber.ZERO));

        /*
            NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT
            Possible values:
            1) 0 or '0' - number or number as string
            2) 5 or '5' - number or number as string
            3) 15 or '15' - number or number as string
            4) 30 or '30' - number or number as string
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT`,
            'NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
            new EzEmployerOptionInfo(
                'NOTIFY_EMPLOYER_EMPLOYEE_MISSED_CLOCK_IN_CLOCK_OUT',
                EzNumber.ZERO.toString(),
                EzNumber.ZERO));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS`,
            'NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
            new EzEmployerOptionInfo(
                'NOTIFY_EMPLOYEE_BEFORE_SHIFT_STARTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES`,
            'ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
            new EzEmployerOptionInfo(
                'ALLOW_EMPLOYEES_TO_SEE_COWORKER_SCHEDULES',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}ALLOW_TIME_OFF_REQUESTS`,
            'ALLOW_TIME_OFF_REQUESTS',
            new EzEmployerOptionInfo(
                'ALLOW_TIME_OFF_REQUESTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}WORK_DAY_START_TIME`,
            'WORK_DAY_START_TIME',
            new EzEmployerOptionInfo(
                'WORK_DAY_START_TIME',
                '00:00:00',
                '00:00:00'));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}WORK_WEEK_START_TIME`,
            'WORK_WEEK_START_TIME',
            new EzEmployerOptionInfo(
                'WORK_WEEK_START_TIME',
                '00:00:00',
                '00:00:00'));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TOTAL_HOURS_IN_WORK_WEEK`,
            'TOTAL_HOURS_IN_WORK_WEEK',
            new EzEmployerOptionInfo(
                'TOTAL_HOURS_IN_WORK_WEEK',
                EzNumber.FOURTY.toString(),
                EzNumber.FOURTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TOTAL_HOURS_IN_WORK_DAY`,
            'TOTAL_HOURS_IN_WORK_DAY',
            new EzEmployerOptionInfo(
                'TOTAL_HOURS_IN_WORK_DAY',
                EzNumber.EIGHT.toString(),
                EzNumber.EIGHT));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}MIDNIGHT`,
            'MIDNIGHT',
            new EzEmployerOptionInfo(
                'MIDNIGHT',
                '23:59:59',
                '23:59:59'));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION`,
            'RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
            new EzEmployerOptionInfo(
                'RESTRICT_CLOCK_IN_OUT_TO_RADIUS_WITHIN_JOB_LOCATION',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            DISTANCE_CLOCK_IN_OUT_WITHIN_JOB_LOCATION
            Possible values:
            1) A number from 100 to 9999 as a number of the number as a string.
            Default value: 100
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}RESTRICTED_DISTANCE_CLOCK_IN_OUT_WITHIN_JOB_LOCATION`,
            'RESTRICTED_DISTANCE_CLOCK_IN_OUT_WITHIN_JOB_LOCATION',
            new EzEmployerOptionInfo(
                'RESTRICTED_DISTANCE_CLOCK_IN_OUT_WITHIN_JOB_LOCATION',
                EzNumber.ONE_HUNDRED.toString(),
                EzNumber.ONE_HUNDRED));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}SELECTED_PAY_PERIOD`,
            'SELECTED_PAY_PERIOD',
            new EzEmployerOptionInfo(
                'SELECTED_PAY_PERIOD',
                EzPayPeriod.NONE,
                EzPayPeriod.NONE));

        const defaultPayPeriodLastClosingDate = EzObject.isValid(globalThis?.ezApi?.ezclocker?.ezDateTime)
            ? globalThis.ezApi.ezclocker.ezDateTime.ezMinusDays(
                globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay(),
                1)
            : moment().subtract(1, 'days');

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}PAY_PERIOD_LAST_CLOSING_DATE`,
            'PAY_PERIOD_LAST_CLOSING_DATE',
            new EzEmployerOptionInfo(
                'PAY_PERIOD_LAST_CLOSING_DATE',
                defaultPayPeriodLastClosingDate.format(),
                defaultPayPeriodLastClosingDate));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TIME_OFF_RESTRICT_REQUEST_START_DATE`,
            'TIME_OFF_RESTRICT_REQUEST_START_DATE',
            new EzEmployerOptionInfo(
                'TIME_OFF_RESTRICT_REQUEST_START_DATE',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        /*
            EZOPTION_TIME_OFF_MINIMUM_DAYS_IN_FUTURE
            This option can have the following Possible values and value types.
            1) 'NONE' - a string
            2) 3 or '3' - a number or number as string
            3) 7 or '7' - a number or number as string
            4) 14 or '14' - a number or number as string
            5) 30 or '30' - a number or number as string
        */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}TIME_OFF_MINIMUM_DAYS_IN_FUTURE`,
            'TIME_OFF_MINIMUM_DAYS_IN_FUTURE',
            new EzEmployerOptionInfo(
                'TIME_OFF_MINIMUM_DAYS_IN_FUTURE',
                'NONE',
                'NONE'));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            ENGINEERING NOTES
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Each account type will transform EzEmployerOption.EZOPTION_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD to their own
            specific value.

                Employer Accounts Signed In with Role:
                    1) ROLE_EMPLOYER: EzEmployerOption.EZOPTION_EMPLOYER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD

                Payroll Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as a restricted ROLE_EMPLOYER): EzEmployerOption.EZOPTION_PAYROLL_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD
                    2) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Manager Accounts Signed In with Role:
                    1) ROLE_MANAGER (acting as ROLE_EMPLOYER): EzEmployerOption.EZOPTION_MANAGER_COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Employee Accounts Signed In with Role:
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)

                Personal Accounts Signed In with Role:
                    1) ROLE_PERSIONAL: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)
                    1) ROLE_EMPLOYEE: NOT SUPPORTED (will result in EzEmployerOption.EZOPTION_SELECTED_INTEGRATION)
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD`,
            'COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
            new EzEmployerOptionInfo(
                'COPY_NOTES_WHEN_SCHEDULE_COPY_FORWARD',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));

        /*
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
            Deprecated constants
            ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        */

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_EMPLOYEE_ID_KEY`,
            'EMPLOYER_SELECTED_EMPLOYEE_ID_KEY',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_EMPLOYEE_ID_KEY',
                EzNumber.MINUS_ONE.toString(),
                EzNumber.MINUS_ONE));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_ENDDATE_KEY`,
            'EMPLOYER_SELECTED_ENDDATE_KEY',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_ENDDATE_KEY',
                '0',
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_SELECTED_PERIOD_KEY`,
            'EMPLOYER_SELECTED_PERIOD_KEY',
            new EzEmployerOptionInfo(
                'EMPLOYER_SELECTED_PERIOD_KEY',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK`,
            'EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
            new EzEmployerOptionInfo(
                'EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK',
                EzEmployerOption.EZOPTION_VALUE_EMPTY,
                EzEmployerOption.EZOPTION_VALUE_EMPTY));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS`,
            'EMPLOYER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYER_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}EMPLOYER_USE_24HOURTIME_IN_EXPORT_REPORTS`,
            'EMPLOYER_USE_24HOURTIME_IN_EXPORT_REPORTS',
            new EzEmployerOptionInfo(
                'EMPLOYER_USE_24HOURTIME_IN_EXPORT_REPORTS',
                EzEmployerOption.EZOPTION_VALUE_FALSE,
                false));

        this.#ezEmployerOptionEnumerationSingleton.ezBuildEnumProp(
            `${ezOptionPrefix}NOTIFY_TIME_OFF_REQUESTS_UPDATED`,
            'NOTIFY_TIME_OFF_REQUESTS_UPDATED',
            new EzEmployerOptionInfo(
                'NOTIFY_TIME_OFF_REQUESTS_UPDATED',
                EzEmployerOption.EZOPTION_VALUE_TRUE,
                true));
    }

    /**
     * @static
     * Static Initialization for EzEmployerOption enumeration class
     */
    static {
        if (null == EzEmployerOption.#ezEmployerOptionEnumerationSingleton) {
            EzEmployerOption.#ezInit();
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
